import { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Input,
  Row,
  Typography,
} from "antd";
import CenteredPopconfirm from "../../components/Common/CenteredPopconfirm";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  retrieveAssets,
  deleteAsset,
  clearAssetFeedback,
} from "../../store/Assets/AssetSlice.js";
import { getJwtToken } from "../../utilities/authentication";
import { useCrudAccess } from "../../hooks/usePageAccess";
import useTranslation from "../../localization/useTranslation";
import appToast from "../../utilities/appToast";
import formatCurrency from "../../utilities/formatCurrency";
import ListPageShell from "../../components/Layout/ListPageShell";
import http from "../../store/services/http-common";
import ResponsiveEntityTable from "../../components/List/ResponsiveEntityTable";
import RecordViewModal, { useRecordViewModal, ViewRecordButton } from "../../components/List/RecordViewModal";
import { useNavigate } from "react-router-dom";

const Assets = () => {
  const { assets, loading, error, successMessage, total } =
    useSelector((state) => state.assets);
  const dispatch = useDispatch();
  const { canList, canCreate, canEdit, canDelete } = useCrudAccess("assets");
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { viewRecord, openView, closeView } = useRecordViewModal();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState("");
  const [searchNotes, setSearchNotes] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ name: "", notes: "" });
  const filtersDebounceRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [statsVersion, setStatsVersion] = useState(0);

  useEffect(() => {
    if (!canList) {
      return undefined;
    }

    let cancelled = false;
    const params = {};
    if (appliedFilters.name) params.name = appliedFilters.name;
    if (appliedFilters.notes) params.notes = appliedFilters.notes;

    http
      .get("/assets/stats", { params })
      .then((response) => {
        if (!cancelled) setStats(response?.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });

    return () => {
      cancelled = true;
    };
  }, [canList, appliedFilters, statsVersion]);

  const assetRows = (Array.isArray(assets) ? assets : []).map((asset) => ({
    ...asset,
    key: asset.id,
  }));

  const columns = [
    {
      title: t("assets.table.name"),
      dataIndex: "name",
    },
    {
      title: t("assets.table.cost"),
      dataIndex: "cost",
      render: (value, record) => (
        <Typography.Text>
          {formatCurrency(value, record.currency ?? "EGP", language)}
        </Typography.Text>
      ),
    },
    {
      title: t("assets.table.notes"),
      dataIndex: "notes",
    },
    {
      title: t("assets.table.attachments"),
      dataIndex: "attachments_count",
      render: (value) =>
        value > 0 ? (
          <Typography.Text>
            <PaperClipOutlined /> {value}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  columns.push({
    title: t("assets.table.actions"),
    dataIndex: "actions",
    key: "actions",
    render: (_, record) => (
      <Row gutter={3}>
        <Col>
          <ViewRecordButton onClick={() => openView(record)} />
        </Col>
        {canEdit && (
          <Col>
            <Button
              icon={<EditOutlined />}
              type="primary"
              onClick={() => navigate(`/setting/assets/${record.id}/edit`)}
            >
              {t("common.edit")}
            </Button>
          </Col>
        )}
        {canDelete && (
          <Col>
            <CenteredPopconfirm
              title={t("assets.confirm.deleteTitle")}
              description={t("assets.confirm.deleteMessage")}
              onConfirm={() => handleDeleteAsset(record.id)}
              okText={t("common.deleteConfirm")}
              cancelText={t("common.back")}
            >
              <Button icon={<DeleteOutlined />} type="primary" danger>
                {t("common.delete")}
              </Button>
            </CenteredPopconfirm>
          </Col>
        )}
      </Row>
    ),
  });

  const handleDeleteAsset = (id) => {
    if (!canDelete) {
      return;
    }

    dispatch(deleteAsset({ id }));
  };

  const initFetch = useCallback(
    (page = 1, per_page = 10, filters = {}) => {
      if (!canList) {
        return;
      }

      const token = getJwtToken();
      const params = { page, per_page };
      if (filters.name) params.name = filters.name;
      if (filters.notes) params.notes = filters.notes;

      dispatch(retrieveAssets({ ...params, token }));
    },
    [dispatch, canList],
  );

  useEffect(() => {
    initFetch(currentPage, pageSize, appliedFilters);
  }, [initFetch, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    if (successMessage) {
      appToast.success(successMessage);
      initFetch(currentPage, pageSize, appliedFilters);
      setStatsVersion((v) => v + 1);
      dispatch(clearAssetFeedback());
    }
  }, [
    successMessage,
    dispatch,
    initFetch,
    currentPage,
    pageSize,
    appliedFilters,
  ]);

  useEffect(() => {
    if (filtersDebounceRef.current) {
      clearTimeout(filtersDebounceRef.current);
    }

    filtersDebounceRef.current = setTimeout(() => {
      setAppliedFilters((prev) => {
        if (prev.name === searchName && prev.notes === searchNotes) {
          return prev;
        }
        setCurrentPage(1);
        return { name: searchName, notes: searchNotes };
      });
    }, 400);

    return () => clearTimeout(filtersDebounceRef.current);
  }, [searchName, searchNotes]);

  if (!canList) {
    return (
      <Alert message={t("assets.alert.noAccess")} type="warning" showIcon />
    );
  }

  return (
    <>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <ListPageShell
        icon={<WalletOutlined />}
        title={t("assets.title") || t("sidebar.assets") || "Assets"}
        subtitle={
          t("assets.subtitle") ||
          "Track laboratory assets, purchase costs, and notes"
        }
        count={total}
        countLabel={t("assets.title") || t("sidebar.assets") || "Assets"}
        extraBadges={
          <span className="list-page-count-badge">
            {t("assets.stats.totalAssets", "Total assets")}:{" "}
            {formatCurrency(stats?.total_cost ?? 0, "EGP", language)}
          </span>
        }
        actions={
          canCreate ? (
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={() => navigate("/setting/assets/create")}
            >
              {t("assets.button.add")}
            </Button>
          ) : null
        }
        filters={
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder={
                  t("assets.search.namePlaceholder") || "Search by name"
                }
                allowClear
                onSearch={(v) => setSearchName(v)}
                onChange={(e) => setSearchName(e.target.value)}
                style={{ width: "100%" }}
                value={searchName}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder={
                  t("assets.search.notesPlaceholder") || "Search by notes"
                }
                allowClear
                onSearch={(v) => setSearchNotes(v)}
                onChange={(e) => setSearchNotes(e.target.value)}
                style={{ width: "100%" }}
                value={searchNotes}
              />
            </Col>
          </Row>
        }
        filtersActiveCount={[searchName, searchNotes].filter(Boolean).length}
        onResetFilters={() => {
          setSearchName("");
          setSearchNotes("");
        }}
      >
        <ResponsiveEntityTable
          loading={loading}
          columns={columns}
          dataSource={assetRows}
          scroll={{ x: 720 }}
          mobileTitleKey="name"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "15", "20", "50"],
          }}
          onChange={(pagination) => {
            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
              setPageSize(pagination.pageSize);
              setCurrentPage(1);
              return;
            }
            if (pagination?.current) {
              setCurrentPage(pagination.current);
            }
          }}
        />
        <RecordViewModal record={viewRecord} columns={columns} title={(r) => r.name} onClose={closeView} />
      </ListPageShell>
    </>
  );
};

export default Assets;
